import { useMemo } from "react";
import { Control, Controller, type FieldValues } from "react-hook-form";

import { DatePicker as BaseDatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { CalendarIcon } from "@mui/x-date-pickers";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { useTheme } from "@mui/material/styles";

import { InputLabelHeight } from "@covertree/types";
import { getInputLabelHeight } from "@covertree/utils";

interface DatePickerProps<TValues extends FieldValues> {
  id: string;
  name: string;
  label: React.ReactNode;
  control: Control<TValues>;
  disabled?: boolean;
  labelHeight?: InputLabelHeight;
  dataTestId: string;
  disableFuture?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** Block keyboard editing of the text field while keeping the calendar usable. */
  readOnly?: boolean;
  fontSize?: number;
  labelFontSize?: number;
}

export function FormDatePicker<TValues extends FieldValues>({
  id,
  name,
  label,
  control,
  disabled = false,
  labelHeight = InputLabelHeight.DEFAULT,
  dataTestId,
  disableFuture,
  minDate,
  maxDate = new Date(),
  readOnly = false,
  fontSize,
  labelFontSize,
}: DatePickerProps<TValues>) {
  const theme = useTheme();
  const fontSizeValue = fontSize ? `${fontSize}px` : undefined;

  const labelComponentHeight = useMemo(
    () => getInputLabelHeight(theme, labelHeight),
    [labelHeight, theme]
  );

  return (
    <FormControl variant="standard" fullWidth>
      <InputLabel
        htmlFor={id}
        id={`${id}-label`}
        sx={{
          position: "static",
          alignItems: "flex-end",
          display: "flex",
          transform: "none",
          height: labelComponentHeight,
          "& > p": {
            display: "flex",
            alignItems: "center",
            flex: 1,
            width: "100%",
            whiteSpace: "break-spaces",
          },
        }}>
        <Typography
          variant="body2"
          sx={{ margin: 0 }}
          fontSize={labelFontSize ?? fontSize}>
          {label}
        </Typography>
      </InputLabel>

      <Controller
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        name={name as any}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <BaseDatePicker
              data-testid={dataTestId}
              disabled={disabled}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
              slots={{
                openPickerIcon: (props) => (
                  <CalendarIcon
                    sx={{
                      width: fontSize
                        ? `${fontSize * 2}px`
                        : theme.spacing(4.5),
                      height: fontSize
                        ? `${fontSize * 2}px`
                        : theme.spacing(4.5),
                      padding: fontSize
                        ? `${fontSize * 0.375}px`
                        : theme.spacing(0.75),
                      ...(fontSize && { fontSize: `${fontSize * 1.25}px` }),
                    }}
                    {...props}
                  />
                ),
              }}
              views={["year", "month", "day"]}
              sx={{
                "& button": {
                  marginRight: 0,
                },
              }}
              slotProps={{
                openPickerButton: {
                  sx: {
                    marginRight: theme.spacing(-0.5),
                  },
                },
                textField: {
                  error: !!error,
                  ...(fontSize && {
                    sx: {
                      fontSize: fontSizeValue,
                      "& .MuiInputBase-root": {
                        height: fontSize * 3,
                      },
                      "& .MuiOutlinedInput-root": {
                        minHeight: fontSize * 3,
                      },
                    },
                  }),
                  ...(readOnly
                    ? {
                        inputProps: {
                          readOnly: true,
                          ...(fontSize && {
                            style: { fontSize: fontSizeValue },
                          }),
                        },
                      }
                    : {
                        ...(fontSize && {
                          inputProps: { style: { fontSize: fontSizeValue } },
                        }),
                      }),
                },
              }}
              {...field}
            />
            <FormHelperText
              error
              {...(!error?.message && { sx: { margin: 0 } })}>
              {error?.message}
            </FormHelperText>
          </LocalizationProvider>
        )}
      />
    </FormControl>
  );
}
